import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Divider,
  Paper,
  Grid,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Download from '@mui/icons-material/Download';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { useCart } from '../../contexts/CartContext';
import { generateSignedAgreementPDF } from '../../utils/pdfGenerator';

interface CartPageProps {
  onNavigate: (path: string) => void;
}

export const CartPage: React.FC<CartPageProps> = ({ onNavigate }) => {
  const { cart, removeFromCart, updateQuantity, updatePartialPayment, itemCount, hasActivityPass } = useCart();

  // Tour-driven cart cleanup. The activity-pass tour step (7) removes
  // the item it added before advancing to step 8 so the cart-seeded
  // step starts from a clean slate.
  useEffect(() => {
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.trigger !== 'cart-remove-activity-pass') return;
      const passItem = cart.items.find((it) => it.fee.isActivityPass);
      if (passItem) removeFromCart(passItem.id);
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, [cart.items, removeFromCart]);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity > 0) {
      updateQuantity(itemId, newQuantity);
    }
  };

  const handlePartialPaymentChange = (itemId: string, amount: string) => {
    if (amount === '') {
      updatePartialPayment(itemId, undefined);
    } else {
      const numAmount = parseFloat(amount);
      if (!isNaN(numAmount)) {
        updatePartialPayment(itemId, numAmount);
      }
    }
  };

  if (cart.items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            p: { xs: 4, sm: 6, md: 8 },
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.03) 0%, rgba(8, 145, 178, 0.03) 100%)',
            border: '2px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              p: { xs: 2, md: 3 },
              borderRadius: '50%',
              bgcolor: 'grey.100',
              mb: { xs: 2, md: 3 },
            }}
          >
            <ShoppingCartOutlined sx={{ fontSize: { xs: 48, md: 64 }, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h4" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, mb: { xs: 3, md: 4 }, maxWidth: 400, mx: 'auto' }}>
            Browse our catalog to add items to your cart
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => onNavigate('/catalog')}
            startIcon={<ShoppingBag />}
            sx={{ px: { xs: 3, md: 4 }, py: { xs: 1.25, md: 1.5 }, fontSize: { xs: '0.9rem', md: '1rem' } }}
          >
            Browse Catalog
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ShoppingCartOutlined sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
              }}
            >
              Shopping Cart
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
            }}
          >
            {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 8 }} data-tour="cart-items">
          {cart.items.map(item => (
            <Card
              key={item.id}
              {...(item.fee.isActivityPass ? { 'data-tour': 'cart-activity-pass-item' } : {})}
              sx={{ mb: 2 }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2 } }}>
                <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="center">
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        {item.fee.name}
                      </Typography>
                      {item.fee.isActivityPass && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            bgcolor: '#fef3c7',
                            color: '#92400e',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        >
                          <ConfirmationNumber sx={{ fontSize: 14 }} />
                          Activity Pass
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
                      {item.fee.description}
                    </Typography>
                    {item.fee.activityPassPricing?.enabled && hasActivityPass && item.activityPassQuantityUsed && item.activityPassQuantityUsed > 0 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid', borderColor: '#bbf7d0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <ConfirmationNumber sx={{ fontSize: 14, color: 'success.main' }} />
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            Activity Pass pricing applied
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.activityPassQuantityUsed} x ${(item.fee.activityPassPricing.isFree ? 0 : item.fee.activityPassPricing.price).toFixed(2)} (Activity Pass)
                          {item.quantity > item.activityPassQuantityUsed && (
                            <> + {item.quantity - item.activityPassQuantityUsed} x ${item.fee.basePrice.toFixed(2)} (Regular)</>
                          )}
                        </Typography>
                        {item.activityPassDiscount && item.activityPassDiscount > 0 && (
                          <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>
                            You save: ${item.activityPassDiscount.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {item.questionnaireResponses && Object.keys(item.questionnaireResponses).filter(key => key !== 'ticketType').length > 0 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        {Object.entries(item.questionnaireResponses)
                          .filter(([key]) => key !== 'ticketType')
                          .map(([key, value]) => {
                            const question = item.fee.questionnaire?.find(q => q.id === key);
                            if (!question) return null;

                            let displayValue = value;
                            if (Array.isArray(value)) {
                              displayValue = value.join(', ');
                            } else if (question.options) {
                              const option = question.options.find(o => o.value === value);
                              displayValue = option?.label || value;
                            }

                            return (
                              <Typography key={key} variant="caption" sx={{ display: 'block', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                <strong>{question.label}:</strong> {String(displayValue)}
                              </Typography>
                            );
                          })}
                      </Box>
                    )}
                    {item.signatureData && item.fee.termsAndConditions && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, color: 'success.dark' }}>
                              Terms Accepted
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            startIcon={<Download sx={{ fontSize: 14 }} />}
                            onClick={async () => await generateSignedAgreementPDF(item.fee.name, item.fee.termsAndConditions!, item.signatureData!, item.fee.course)}
                            sx={{
                              fontSize: { xs: '0.65rem', md: '0.7rem' },
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                            }}
                          >
                            Download
                          </Button>
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'text.secondary' }}>
                          <strong>Signed by:</strong> {item.signatureData.signedBy}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'text.secondary' }}>
                          <strong>Date:</strong> {new Date(item.signatureData.signedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 4, sm: 2 }}>
                    {item.questionnaireResponses?.ticketType ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                          Ticket Type
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                          {item.questionnaireResponses.ticketType}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} color="primary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, mt: 0.5 }}>
                          ${item.unitPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                          {item.amountPaid && item.amountPaid > 0 ? 'Remaining Balance' : 'Your Price'}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          ${item.unitPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 4, sm: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                      Quantity
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          width: { xs: 24, md: 32 },
                          height: { xs: 24, md: 32 }
                        }}
                      >
                        <Remove sx={{ fontSize: { xs: 14, md: 18 } }} />
                      </IconButton>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={e =>
                          handleQuantityChange(item.id, parseInt(e.target.value) || 1)
                        }
                        inputProps={{
                          min: 1,
                          max: item.fee.maxQuantity || 999,
                          style: { textAlign: 'center', fontSize: '0.875rem', padding: '6px 8px' }
                        }}
                        sx={{ width: { xs: 60, md: 80 } }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={item.fee.maxQuantity ? item.quantity >= item.fee.maxQuantity : false}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          width: { xs: 24, md: 32 },
                          height: { xs: 24, md: 32 }
                        }}
                      >
                        <Add sx={{ fontSize: { xs: 14, md: 18 } }} />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 3, sm: 2 }} data-tour="cart-partial-payment">
                    {item.fee.allowsPartialPayment && item.partialPaymentPercentage !== undefined ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                          {item.amountPaid && item.amountPaid > 0 ? 'Remaining Balance' : 'Your Price'}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                          ${(item.partialPaymentAmount ?? item.totalPrice).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: { xs: '0.65rem', md: '0.7rem' } }}>
                          {item.partialPaymentPercentage}% of ${(item.amountPaid && item.amountPaid > 0 ? ((item.unitPrice * item.quantity) - item.amountPaid) : item.totalPrice).toFixed(2)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                          Total
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                          ${item.totalPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 1, sm: 1 }} sx={{ textAlign: 'right' }}>
                    <IconButton
                      {...(item.fee.isActivityPass ? { 'data-tour': 'cart-activity-pass-remove' } : {})}
                      color="error"
                      onClick={() => removeFromCart(item.id)}
                      size="small"
                    >
                      <Delete sx={{ fontSize: { xs: 18, md: 24 } }} />
                    </IconButton>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          ))}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              position: { xs: 'relative', md: 'sticky' },
              top: { xs: 0, md: 96 },
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight={700}>
              Order Summary
            </Typography>

            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${cart.subtotal.toFixed(2)}
                </Typography>
              </Box>
              {hasActivityPass && cart.items.some(item => item.fee.activityPassPricing?.enabled) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, py: 0.5, px: 1, bgcolor: '#f0fdf4', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ConfirmationNumber sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="body2" color="success.main" fontWeight={500}>
                      Activity Pass Savings
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    Applied
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax (7%):</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${cart.tax.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={600}>
                  Total:
                </Typography>
                <Typography variant="h5" fontWeight={600} color="primary">
                  ${cart.total.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => onNavigate('/checkout')}
              sx={{ mt: 2 }}
            >
              Proceed to Checkout
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => onNavigate('/catalog')}
              sx={{ mt: 2 }}
            >
              Continue Shopping
            </Button>

            <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                Cart Details
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Items: {itemCount}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Last Updated: {cart.updatedAt.toLocaleString()}
              </Typography>
            </Paper>
          </Paper>
        </Grid>
      </Grid>
      </Container>
    </>
  );
};
